import { useCallback, useRef } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { Button, Dropdown } from "semantic-ui-react";
import styled from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";

import api from "api";
import { useAppSelector } from "store";
import { useContainerDimensions } from "utils/useContainerDimensions";
import util from "utils/utils";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas";

import Impact from "./Impact";

const IdeaImpactHeader = styled.div`
  > span {
    display: block;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }
  .impact-name {
  }
  .impact-description {
    opacity: 0.7;
  }
`;

const IdeaImpactDropdownItem = styled(Dropdown.Item)`
  display: flex !important;
  overflow: hidden;
  text-overflow: ellipsis;
  > i {
    width: 10px;
  }
  ${IdeaImpactHeader} {
    max-width: 95%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: normal;
  }
`;

type ImpactProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"];
  impactList: OpenAPI.Schemas["OrganisationImpact"][];
  canManage: boolean;
};

const ImpactManagement = ({ idea, impactList, canManage }: ImpactProps) => {
  const ideaId = idea._id;
  const dropdownRef = useRef(null);
  const { width } = useContainerDimensions(dropdownRef);
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const queryParams = useIdeaQueryParams("board");
  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [idea.challenge, queryParams], {
      select: (data) => data.ideaImpacts?.[idea._id],
    }),
  );

  const addImpact = useCallback(
    (impactId) => {
      api.ideas.addImpactMeasure(
        ideaId,
        impactId,
        (data) => {
          api.setQueryData(
            api.challenges.getIdeaImpacts,
            [idea.challenge],
            (prev) => ({
              ...(prev || {}),
              ideaImpacts: {
                ...(prev?.ideaImpacts || {}),
                [idea._id]: {
                  ...(prev?.ideaImpacts?.[idea._id] || {}),
                  totalImpacts: [
                    ...(prev?.ideaImpacts?.[idea._id]?.totalImpacts ?? []),
                    data.newImpact,
                  ],
                },
              },
            }),
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [ideaId, idea],
  );

  const deleteImpact = useCallback(
    (impactId, impactMeasureId) => {
      util
        .confirm(
          `Are you sure you want to delete this ${t("generic.impact")} measure?`,
          "This action cannot be undone.",
        )
        .then(() => {
          api.ideas.removeImpactMeasure(
            idea._id,
            impactId,
            impactMeasureId,
            ({ totalImpacts }) => {
              api.setQueryData(
                api.challenges.getIdeaImpacts,
                [idea.challenge],
                (
                  prev: OpenAPI.GET<"/challenges/{id}/ideas/impacts">["response"],
                ) => ({
                  ...(prev || {}),
                  ideaImpacts: {
                    ...(prev?.ideaImpacts || {}),
                    [idea._id]: {
                      ...(prev?.ideaImpacts?.[idea._id] || {}),
                      totalImpacts,
                    },
                  },
                }),
              );
            },
            (err) => {
              toast.error(err.message);
            },
          );
        })
        .catch(() => {});
    },
    [idea, ideaId, t],
  );

  const onUpdateIdeaImpact = useCallback(
    (impactId, data) => {
      api.setQueryData(
        api.challenges.getIdeaImpacts,
        [idea.challenge],
        (prev) => ({
          ...prev,
          ideaImpacts: {
            ...(prev?.ideaImpacts || {}),
            [idea._id]: {
              ...(prev?.ideaImpacts?.[idea._id] || {}),
              totalImpacts: prev?.ideaImpacts?.[idea._id]?.totalImpacts?.map(
                (impact) => {
                  if (impact.impactId === impactId) {
                    return { ...impact, ...data };
                  }
                  return impact;
                },
              ),
            },
          },
        }),
      );
    },
    [idea],
  );

  const onUpdateIdeaTotalImpacts = useCallback(
    (totalImpacts) => {
      api.setQueryData(
        api.challenges.getIdeaImpacts,
        [idea.challenge],
        (prev) => ({
          ...(prev || {}),
          ideaImpacts: {
            ...(prev?.ideaImpacts || {}),
            [idea._id]: {
              ...(prev?.ideaImpacts?.[idea._id] || {}),
              totalImpacts,
            },
          },
        }),
      );
    },
    [idea],
  );

  if (!(impactList?.length > 0)) return null;
  return (
    <div ref={dropdownRef}>
      <p>
        How will this {t("generic.idea")} {t("generic.impact")} the{" "}
        {t("generic.challenge")}?
      </p>

      {(
        canManage ||
        util.hasPermission(
          user,
          "challenge.editProjectImpacts",
          idea?.challenge,
        )
      ) ?
        <Dropdown
          icon={null}
          pointing="top"
          fluid
          trigger={
            <Button
              size="small"
              primary
              icon="plus"
              content={`Add new ${t("generic.impact")}`}
            />
          }
        >
          <Dropdown.Menu
            style={{
              left: "auto",
              right: "auto",
              width,
              maxHeight: 250,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {impactList.map((i) => (
              <IdeaImpactDropdownItem
                key={i.name}
                icon={i.icon ?? "tag"}
                content={
                  <IdeaImpactHeader>
                    <span className="impact-name">{i.name}</span>
                    <span className="impact-description">{i.description}</span>
                  </IdeaImpactHeader>
                }
                // description={i.description}
                onClick={() => addImpact(i._id)}
                style={{ width }}
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      : null}

      {ideaImpactsQuery.data?.totalImpacts?.map((i) => (
        <Impact
          key={i._id}
          idea={idea}
          impact={i}
          onUpdateIdeaImpact={onUpdateIdeaImpact}
          deleteImpact={deleteImpact}
          onUpdateIdeaTotalImpacts={onUpdateIdeaTotalImpacts}
          canEdit={
            canManage ||
            util.hasPermission(
              user,
              "challenge.editProjectImpacts",
              idea?.challenge,
            )
          }
        />
      ))}
    </div>
  );
};

export default ImpactManagement;
